import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IconRegularTranslate, Link, Text } from '@notino/react-styleguide';
import { AuthorType, GetReviewsQuery } from '@notino/shared/definitions/types';

import { Flex } from '@components/Flex';
import { PortalTooltip } from '@components/PortalTooltip';
import { StyledIconRegularInfo } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/FetchDoneBlock/ReviewItemDisplay/styled';
import { useTranslateOrNull } from '@hooks/useTranslateOrNull';
import { styled } from '@linaria/react';

import pdMessages from '../../containers/ProductDetailContainer/messages';
import { messages as reviewMessages } from '../../containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/messages';

export const TooltipContent = styled.div`
  max-width: max-content;
  width: 15rem;
  padding: 0.5rem;
`;

export const MoreInfoLink = styled(Link)`
  margin-left: 0.25rem;
  text-decoration: underline;
`;

const TranslationButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;

  &:hover {
    text-decoration: none;
  }
`;

export const useGetReviewLabelComponents = () => {
  const { formatMessage } = useIntl();
  const translate = useTranslateOrNull();

  return (
    review: NonNullable<
      NonNullable<GetReviewsQuery['reviewsAndFilters']>['data']
    >['reviews'][number],
    showOriginal?: boolean,
    toggleShowOriginal?: () => void
  ) => {
    const getMoreLinkComponent = (href: string) => {
      if (!href) {
        return null;
      }
      return (
        <MoreInfoLink href={href} rel="nofollow" target="__blank">
          {formatMessage(reviewMessages.reviewMoreText)}
        </MoreInfoLink>
      );
    };

    const isPartner =
      review.authorType === AuthorType.Partner ||
      review.authorType === AuthorType.VerifiedPartner;

    const getPartnerMoreLinkComponent = () => {
      const moreInfoLink = formatMessage(
        reviewMessages[`review${String(review.authorType)}MoreLink`]
      );

      if (moreInfoLink && moreInfoLink !== 'null') {
        return getMoreLinkComponent(moreInfoLink);
      }
    };

    const agencyTooltip = review.agency?.name
      ? translate(`pd.reviews.testedBy.${review.agency.name}.tooltip`)
      : null;

    const agencyInfo = review.agency?.name
      ? translate(`pd.reviews.testedBy.${review.agency.name}`)
      : null;

    return [
      review.originalShopId && !review.sourceLanguage && (
        <Text
          typography="labelSmall400"
          color="text.secondary"
          key="originalShopId"
        >
          <FormattedMessage
            {...reviewMessages.originallyPostedOn}
            values={{
              shopId: review.originalShopId,
              b: (chunks: React.ReactNode) => (
                <b style={{ fontWeight: 500 }}>{chunks}</b>
              ),
            }}
          />
        </Text>
      ),

      isPartner && (
        <Flex gap="4px" key="partner">
          <PortalTooltip
            interactive={true}
            content={
              <PortalTooltip.Content>
                <TooltipContent>
                  <FormattedMessage
                    {...reviewMessages.reviewPartnerDescription}
                  />
                  {getPartnerMoreLinkComponent()}
                </TooltipContent>
              </PortalTooltip.Content>
            }
          >
            <StyledIconRegularInfo
              width="16px"
              height="16px"
              color="icon.secondary"
            />
          </PortalTooltip>
          <Text typography="labelSmall400" color="text.secondary">
            <FormattedMessage {...reviewMessages.reviewPartnerTitle} />
            {' ・ '}
            <FormattedMessage
              {...reviewMessages.originallyPublishedOn}
              values={{
                website: review.partnerDomain,
                b: (chunks: React.ReactNode) => (
                  <b style={{ fontWeight: 500 }}>{chunks}</b>
                ),
              }}
            />
          </Text>
        </Flex>
      ),

      agencyInfo && (
        <Flex gap="4px" key="partner">
          {agencyTooltip && (
            <PortalTooltip
              interactive={true}
              content={
                <PortalTooltip.Content>
                  <TooltipContent>
                    {agencyTooltip}
                    {getMoreLinkComponent(
                      translate('pd.reviews.verified.more.link') ?? ''
                    )}
                  </TooltipContent>
                </PortalTooltip.Content>
              }
            >
              <StyledIconRegularInfo
                width="16px"
                height="16px"
                color="icon.secondary"
              />
            </PortalTooltip>
          )}
          <Text
            typography="labelSmall400"
            color="text.secondary"
            key="isAgencyTested"
          >
            {agencyInfo}
          </Text>
        </Flex>
      ),

      review.translatedFrom && (
        <Flex gap="8px" align="center">
          <IconRegularTranslate
            width="16px"
            height="16px"
            viewBox="0 0 16 16"
          />
          <Text
            typography="labelSmall400"
            color="text.secondary"
            key="translatedFrom"
            style={{ fontWeight: 500 }}
          >
            <FormattedMessage
              {...pdMessages.reviewSource}
              values={{ shopId: review.translatedFrom }}
            />
          </Text>
        </Flex>
      ),

      review.sourceLanguage && review.originalText && (
        <Flex direction="column" gap="12px">
          <Text
            typography="labelSmall400"
            color="text.secondary"
            key="originalShopId"
          >
            <FormattedMessage
              {...reviewMessages.originallyPostedOn}
              values={{
                shopId: review.originalShopId,
                b: (chunks: React.ReactNode) => (
                  <b style={{ fontWeight: 500 }}>{chunks}</b>
                ),
              }}
            />
          </Text>

          <Flex gap="8px" align="center" key="translatedReviewToggle">
            <IconRegularTranslate
              width="16px"
              height="16px"
              viewBox="0 0 16 16"
            />

            <Text
              typography="labelSmall400"
              color="text.secondary"
              style={{ fontWeight: 500 }}
            >
              {translate(`pd.reviews.sourceLanguage.${review.sourceLanguage}`)}
            </Text>

            <Text typography="labelSmall400" color="text.secondary">
              {' • '}
            </Text>

            <Text typography="labelSmall400" color="text.secondary">
              <TranslationButton onClick={toggleShowOriginal}>
                <FormattedMessage
                  {...reviewMessages[
                    showOriginal ? 'showTranslation' : 'showOriginal'
                  ]}
                />
              </TranslationButton>
            </Text>
          </Flex>
        </Flex>
      ),
    ].filter(Boolean);
  };
};
